import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import SearchField from '../components/SearchField';
import FilterMenu, { dateDimension } from '../components/FilterMenu';
import Sparkline from '../components/Sparkline';
import { Switch } from '@/src/shadComponents/ui/switch';
import { StatusChip } from './jobs/shared';
import {
    IconHome,
    IconActivations,
    IconProduction,
    IconPlus,
    IconBack,
    IconOverview,
    IconGallery,
    IconShare,
    IconCapture,
    IconProcessing,
    IconMedia,
    IconMore,
} from '../components/icons';

/*
 * UI Kit — every v2 element in one place, light and dark side by side,
 * rendered exactly as it ships today (real components where harmless,
 * exact-class replicas where the real thing would navigate or fetch).
 * A mirror, not a makeover: flaws render here too, on purpose, so they
 * can be reviewed and approved one at a time. Route: /v2/kit.
 */

const SAMPLE_SERIES = [4, 9, 6, 14, 11, 19, 16, 24];

/* ---- color lab: live token overrides, preview-only ---- */

const COLOR_TOKENS = [
    { key: 'brand', label: 'brand', hint: 'primary actions, dock active' },
    { key: 'brand-ink', label: 'brand-ink', hint: 'quiet links' },
    { key: 'brand-soft', label: 'brand-soft', hint: 'soft brand fills' },
    { key: 'attention', label: 'attention', hint: 'live red, destructive' },
    { key: 'good', label: 'good', hint: 'online green' },
    { key: 'dock', label: 'dock', hint: 'dock ground' },
] as const;

const TOKEN_DEFAULTS: Record<'light' | 'dark', Record<string, string>> = {
    light: {
        brand: '243 100% 69%',
        'brand-ink': '244 75% 61%',
        'brand-soft': '244 100% 97%',
        attention: '16 77% 60%',
        good: '144 55% 40%',
        dock: '227 17% 10%',
    },
    dark: {
        brand: '244 100% 76%',
        'brand-ink': '244 100% 81%',
        'brand-soft': '233 30% 17%',
        attention: '17 83% 66%',
        good: '142 43% 50%',
        dock: '223 18% 15%',
    },
};

/* "H S% L%" ↔ #rrggbb for the native color input */
function hslTripleToHex(triple: string): string {
    const m = triple.match(/([\d.]+)\s+([\d.]+)%\s+([\d.]+)%/);
    if (!m) return '#000000';
    const h = +m[1] / 360;
    const s = +m[2] / 100;
    const l = +m[3] / 100;
    const f = (n: number) => {
        const k = (n + h * 12) % 12;
        const a = s * Math.min(l, 1 - l);
        const c = l - a * Math.max(-1, Math.min(k - 3, 9 - k, 1));
        return Math.round(c * 255)
            .toString(16)
            .padStart(2, '0');
    };
    return `#${f(0)}${f(8)}${f(4)}`;
}

function hexToHslTriple(hex: string): string {
    const r = parseInt(hex.slice(1, 3), 16) / 255;
    const g = parseInt(hex.slice(3, 5), 16) / 255;
    const b = parseInt(hex.slice(5, 7), 16) / 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const l = (max + min) / 2;
    let h = 0;
    let s = 0;
    if (max !== min) {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        else if (max === g) h = ((b - r) / d + 2) / 6;
        else h = ((r - g) / d + 4) / 6;
    }
    return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(
        l * 100,
    )}%`;
}

/* HSL triple ↔ HSV for the picker surface (pickers are HSV-shaped) */
function tripleToHsv(triple: string): { h: number; s: number; v: number } {
    const m = triple.match(/([\d.]+)\s+([\d.]+)%\s+([\d.]+)%/);
    const h = m ? +m[1] : 0;
    const sl = m ? +m[2] / 100 : 0;
    const l = m ? +m[3] / 100 : 0;
    const v = l + sl * Math.min(l, 1 - l);
    const s = v === 0 ? 0 : 2 * (1 - l / v);
    return { h, s: s * 100, v: v * 100 };
}

function hsvToTriple(h: number, s: number, v: number): string {
    const sv = s / 100;
    const vv = v / 100;
    const l = vv * (1 - sv / 2);
    const sl = l === 0 || l === 1 ? 0 : (vv - l) / Math.min(l, 1 - l);
    return `${Math.round(h)} ${Math.round(sl * 100)}% ${Math.round(
        l * 100,
    )}%`;
}

/*
 * SwatchPicker — the color input in the v2 language: a swatch chip
 * opening our own popover (SV pad + hue rail + hex field), no native
 * browser picker. Applies live while dragging.
 */
function SwatchPicker({
    triple,
    onChange,
    title,
}: {
    triple: string;
    onChange: (triple: string) => void;
    title: string;
}) {
    const [open, setOpen] = useState(false);
    const [hsv, setHsv] = useState(() => tripleToHsv(triple));
    const [hexDraft, setHexDraft] = useState('');
    const rootRef = useRef<HTMLDivElement>(null);
    const padRef = useRef<HTMLDivElement>(null);
    const hueRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (open) {
            setHsv(tripleToHsv(triple));
            setHexDraft(hslTripleToHex(triple));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    useEffect(() => {
        if (!open) return;
        const onDown = (e: MouseEvent) => {
            if (!rootRef.current?.contains(e.target as Node))
                setOpen(false);
        };
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setOpen(false);
        };
        document.addEventListener('mousedown', onDown);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDown);
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);

    const commit = (next: { h: number; s: number; v: number }) => {
        setHsv(next);
        const t = hsvToTriple(next.h, next.s, next.v);
        setHexDraft(hslTripleToHex(t));
        onChange(t);
    };

    const drag = (
        ref: React.RefObject<HTMLDivElement>,
        handler: (x: number, y: number) => void,
    ) => (e: React.PointerEvent) => {
        const el = ref.current;
        if (!el) return;
        e.preventDefault();
        el.setPointerCapture(e.pointerId);
        const move = (ev: PointerEvent | React.PointerEvent) => {
            const r = el.getBoundingClientRect();
            const x = Math.min(
                1,
                Math.max(0, (ev.clientX - r.left) / r.width),
            );
            const y = Math.min(
                1,
                Math.max(0, (ev.clientY - r.top) / r.height),
            );
            handler(x, y);
        };
        move(e);
        const onMove = (ev: PointerEvent) => move(ev);
        const onUp = () => {
            window.removeEventListener('pointermove', onMove);
            window.removeEventListener('pointerup', onUp);
        };
        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
    };

    const applyHex = () => {
        const hex = hexDraft.trim();
        if (!/^#?[0-9a-fA-F]{6}$/.test(hex)) return;
        const norm = hex.startsWith('#') ? hex : `#${hex}`;
        const t = hexToHslTriple(norm);
        setHsv(tripleToHsv(t));
        onChange(t);
    };

    return (
        <div ref={rootRef} className="relative">
            <button
                type="button"
                title={title}
                aria-haspopup="dialog"
                aria-expanded={open}
                onClick={() => setOpen((v) => !v)}
                className="h-7 w-9 cursor-pointer rounded-[7px] border border-border p-0"
                style={{ background: `hsl(${triple})` }}
            />
            {open && (
                <div className="absolute right-0 top-[calc(100%+6px)] z-50 w-[232px] rounded-[14px] border border-border bg-background p-3 shadow-[0_12px_40px_rgba(0,0,0,0.12)]">
                    <div
                        ref={padRef}
                        onPointerDown={drag(padRef, (x, y) =>
                            commit({
                                ...hsv,
                                s: x * 100,
                                v: (1 - y) * 100,
                            }),
                        )}
                        className="relative h-[130px] cursor-crosshair touch-none rounded-[10px] border border-border"
                        style={{
                            background: `linear-gradient(to top, #000, transparent), linear-gradient(to right, #fff, hsl(${hsv.h} 100% 50%))`,
                        }}
                    >
                        <span
                            className="pointer-events-none absolute h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow-[0_0_0_1px_rgba(0,0,0,0.35)]"
                            style={{
                                left: `${hsv.s}%`,
                                top: `${100 - hsv.v}%`,
                            }}
                        />
                    </div>
                    <div
                        ref={hueRef}
                        onPointerDown={drag(hueRef, (x) =>
                            commit({ ...hsv, h: x * 360 }),
                        )}
                        className="relative mt-3 h-3 cursor-pointer touch-none rounded-full"
                        style={{
                            background:
                                'linear-gradient(to right, #f00, #ff0, #0f0, #0ff, #00f, #f0f, #f00)',
                        }}
                    >
                        <span
                            className="pointer-events-none absolute top-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow-[0_0_0_1px_rgba(0,0,0,0.35)]"
                            style={{
                                left: `${(hsv.h / 360) * 100}%`,
                                background: `hsl(${hsv.h} 100% 50%)`,
                            }}
                        />
                    </div>
                    <div className="mt-3 flex items-center gap-2">
                        <input
                            value={hexDraft}
                            onChange={(e) => setHexDraft(e.target.value)}
                            onBlur={applyHex}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') applyHex();
                            }}
                            aria-label="Hex value"
                            className="w-[86px] rounded-[8px] border border-border bg-transparent px-2 py-1.5 font-mono text-[11.5px] text-foreground"
                        />
                        <span className="min-w-0 flex-1 truncate text-right font-mono text-[10px] text-faint">
                            {hsvToTriple(hsv.h, hsv.s, hsv.v)}
                        </span>
                    </div>
                </div>
            )}
        </div>
    );
}

function ColorLab({
    values,
    onChange,
    onReset,
}: {
    values: Record<'light' | 'dark', Record<string, string>>;
    onChange: (mode: 'light' | 'dark', key: string, triple: string) => void;
    onReset: () => void;
}) {
    const dirty =
        JSON.stringify(values) !== JSON.stringify(TOKEN_DEFAULTS);
    const copyCss = async () => {
        const block = (mode: 'light' | 'dark') =>
            COLOR_TOKENS.map(
                (t) => `    --${t.key}: ${values[mode][t.key]};`,
            ).join('\n');
        const css = `/* light (.psv2) */\n${block(
            'light',
        )}\n\n/* dark (.psv2.dark, .dark .psv2) */\n${block('dark')}`;
        try {
            await navigator.clipboard.writeText(css);
            toast('Token CSS copied');
        } catch {
            toast.error('Copy failed');
        }
    };
    return (
        <section className="mb-10 rounded-[14px] border border-border p-5">
            <div className="mb-3 flex items-baseline justify-between gap-3">
                <div>
                    <span className="ps-label">Color lab</span>
                    <p className="mt-1 text-[12px] text-faint">
                        Preview-only — repaints this page (chrome included)
                        until refresh. Copy CSS to keep a palette.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    {dirty && (
                        <button
                            type="button"
                            onClick={onReset}
                            className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[12.5px] font-semibold text-muted-foreground hover:text-foreground"
                        >
                            Reset
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={copyCss}
                        className="ps-pill cursor-pointer px-4 font-[inherit] text-[12.5px] font-semibold text-foreground"
                    >
                        Copy CSS
                    </button>
                </div>
            </div>
            <div className="grid grid-cols-1 gap-x-8 gap-y-2 sm:grid-cols-2">
                {COLOR_TOKENS.map((t) => (
                    <div
                        key={t.key}
                        className="flex items-center gap-3 border-b border-line py-2"
                    >
                        <div className="min-w-0 flex-1">
                            <div className="font-mono text-[10px] font-semibold tracking-[0.06em]">
                                --{t.key}
                            </div>
                            <div className="truncate text-[11px] text-faint">
                                {t.hint}
                            </div>
                        </div>
                        {(['light', 'dark'] as const).map((mode) => (
                            <div
                                key={mode}
                                className="flex items-center gap-1.5"
                            >
                                <span className="font-mono text-[9px] uppercase tracking-[0.1em] text-faint">
                                    {mode === 'light' ? 'L' : 'D'}
                                </span>
                                <SwatchPicker
                                    title={`--${t.key} (${mode}): ${
                                        values[mode][t.key]
                                    }`}
                                    triple={values[mode][t.key]}
                                    onChange={(triple) =>
                                        onChange(mode, t.key, triple)
                                    }
                                />
                            </div>
                        ))}
                    </div>
                ))}
            </div>
        </section>
    );
}

function Duo({
    title,
    name,
    render,
}: {
    title: string;
    name: string;
    render: () => React.ReactNode;
}) {
    return (
        <section className="mb-10">
            <div className="mb-2 flex items-baseline justify-between">
                <span className="ps-label">{title}</span>
                <span className="font-mono text-[10px] text-faint">
                    {name}
                </span>
            </div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <div className="rounded-[14px] border border-border bg-background p-5">
                    {render()}
                </div>
                <div className="psv2 dark rounded-[14px] border border-border bg-background p-5">
                    {render()}
                </div>
            </div>
        </section>
    );
}

/* ---- static replicas (exact classes, no navigation/fetching) ---- */

function DockReplica() {
    const item = (icon: React.ReactNode, active = false) => (
        <span
            className={
                'relative flex h-10 w-10 items-center justify-center rounded-full ' +
                (active ? 'bg-brand text-brand-foreground' : 'text-dock-dim')
            }
        >
            {icon}
        </span>
    );
    return (
        <div className="flex flex-col items-center gap-4">
            <div className="flex items-center gap-0.5 rounded-full bg-dock p-[7px] shadow-[0_10px_34px_-6px_rgba(0,0,0,0.55),0_2px_6px_rgba(0,0,0,0.3)]">
                {item(<IconHome />, true)}
                {item(<IconProduction />)}
                {item(<IconActivations />)}
                <span className="mx-1 h-5 w-px bg-white/10" aria-hidden />
                {item(<IconPlus />)}
            </div>
            <div className="flex items-center gap-0.5 rounded-full bg-dock p-[7px] shadow-[0_10px_34px_-6px_rgba(0,0,0,0.55),0_2px_6px_rgba(0,0,0,0.3)]">
                {item(<IconBack />)}
                <span className="mx-1 h-5 w-px bg-white/10" aria-hidden />
                {item(<IconOverview />, true)}
                {item(<IconGallery />)}
                {item(<IconShare />)}
                {item(<IconCapture />)}
                {item(<IconProcessing />)}
                {item(<IconMedia />)}
                <span className="mx-1 h-5 w-px bg-white/10" aria-hidden />
                {item(<IconMore />)}
            </div>
        </div>
    );
}

function MenuReplica() {
    return (
        <div className="w-[260px] rounded-[13px] border border-border bg-background p-1.5 shadow-lg">
            <div className="mb-1 flex items-center gap-2.5 border-b border-line px-2.5 pb-2.5 pt-1.5">
                <span className="flex h-[31px] w-[31px] flex-none items-center justify-center rounded-full bg-secondary text-[11px] font-semibold text-muted-foreground">
                    JS
                </span>
                <span className="min-w-0 flex-1 truncate text-[13px] font-semibold">
                    josh
                </span>
            </div>
            <div className="flex w-full items-center gap-2 rounded-[8px] px-2.5 py-2 text-[13px] text-muted-foreground">
                <span className="flex-1">Organization</span>
                <span className="max-w-[120px] truncate font-semibold text-foreground">
                    Smilebooth
                </span>
                <span aria-hidden className="text-[10px] text-faint">
                    ▾
                </span>
            </div>
            <div className="flex items-center justify-between gap-2 rounded-[8px] px-2.5 py-2 text-[13px] text-muted-foreground">
                Dark mode
                <Switch checked={false} onCheckedChange={() => {}} />
            </div>
            <div className="mx-1 my-1 border-t border-line" />
            <div className="flex items-center gap-2 rounded-[8px] px-2.5 py-2 text-[13px] text-muted-foreground">
                <span className="h-1.5 w-1.5 flex-none rounded-full bg-transparent" />
                Log out
            </div>
        </div>
    );
}

function JumpReplica() {
    const row = (
        name: string,
        detail: string,
        live = false,
        bold = false,
    ) => (
        <div className="flex w-full items-center gap-2 rounded-[9px] px-2.5 py-2 text-[13px]">
            {live && (
                <span
                    className="ps-pulse ps-pulse-live flex-none"
                    style={{ width: 6, height: 6 }}
                />
            )}
            <span className={'w-0 flex-1 truncate ' + (bold ? 'font-bold' : '')}>
                {name}
            </span>
            <span className="ml-auto flex-none whitespace-nowrap text-right font-mono text-[10px] text-faint">
                {detail}
            </span>
        </div>
    );
    return (
        <div className="w-[280px] rounded-[14px] border border-border bg-background p-1.5 shadow-[0_12px_40px_rgba(0,0,0,0.12)]">
            <div className="mb-1 flex items-center gap-2 border-b border-line px-2.5 pb-2 pt-1">
                <span className="flex-none text-faint">
                    <svg
                        width={14}
                        height={14}
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.7"
                        strokeLinecap="round"
                    >
                        <circle cx="11" cy="11" r="6.5" />
                        <path d="M15.8 15.8 20 20" />
                    </svg>
                </span>
                <span className="w-full text-[13px] text-faint">|</span>
            </div>
            <div className="px-2.5 pb-0.5 pt-2 font-mono text-[9px] font-semibold uppercase tracking-[0.14em] text-faint">
                Live now · 1
            </div>
            {row("Everybody's Somebody Festival", 'LIVE', true, true)}
            <div className="px-2.5 pb-0.5 pt-2 font-mono text-[9px] font-semibold uppercase tracking-[0.14em] text-faint">
                Today & tomorrow
            </div>
            {row('20260816 - Team Epiphany - Grand Marnier', 'TOMORROW')}
        </div>
    );
}

export default function KitV2() {
    const [search, setSearch] = useState('');
    const [switchOn, setSwitchOn] = useState(true);
    const [checked, setChecked] = useState(true);
    const [tokens, setTokens] = useState(TOKEN_DEFAULTS);

    const setToken = (
        mode: 'light' | 'dark',
        key: string,
        triple: string,
    ) =>
        setTokens((t) => ({
            ...t,
            [mode]: { ...t[mode], [key]: triple },
        }));

    /* injected after tokens.css in DOM order, so equal-specificity
       selectors win — live preview without touching the stylesheet */
    const overrideCss = `
.psv2 {
${COLOR_TOKENS.map((t) => `    --${t.key}: ${tokens.light[t.key]};`).join('\n')}
}
.psv2.dark, .dark .psv2 {
${COLOR_TOKENS.map((t) => `    --${t.key}: ${tokens.dark[t.key]};`).join('\n')}
}`;

    const monoTag = (label: string) => (
        <span className="rounded-full border border-border px-2 py-0.5 font-mono text-[9px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
            {label}
        </span>
    );

    return (
        <main className="mx-auto max-w-[1180px] px-[clamp(18px,3vw,34px)] pt-4">
            <div className="ps-label mb-1.5">UI Kit</div>
            <h1 className="text-[23px] font-bold tracking-[-0.02em]">
                Every element, both modes
            </h1>
            <p className="mb-9 mt-1 text-[13px] text-muted-foreground">
                Rendered exactly as the app ships them today — including
                current flaws. Left is light, right is dark. Captions name
                the component or class for review comments.
            </p>

            <style>{overrideCss}</style>

            <ColorLab
                values={tokens}
                onChange={setToken}
                onReset={() => setTokens(TOKEN_DEFAULTS)}
            />

            <Duo
                title="Type — the canonical scale"
                name="floor: 9px ambient · 10px informational · 16px touch inputs"
                render={() => (
                    <div className="flex flex-col gap-2.5">
                        {(
                            [
                                [
                                    '23px · bold · -0.02em — page title',
                                    <span className="text-[23px] font-bold leading-tight tracking-[-0.02em]">
                                        Page title
                                    </span>,
                                ],
                                [
                                    '20px · bold — card title',
                                    <span className="text-[20px] font-bold tracking-[-0.02em]">
                                        Card title
                                    </span>,
                                ],
                                [
                                    '19px · bold · tabular — stat value',
                                    <span className="text-[19px] font-bold tabular-nums tracking-[-0.01em]">
                                        29,628
                                    </span>,
                                ],
                                [
                                    '13.5px · semibold — row name',
                                    <span className="text-[13.5px] font-semibold">
                                        Row name
                                    </span>,
                                ],
                                [
                                    '13px · muted — body / metadata',
                                    <span className="text-[13px] text-muted-foreground">
                                        Body and metadata text
                                    </span>,
                                ],
                                [
                                    '12.5px · semibold — quiet link / inline error',
                                    <span className="text-[12.5px] font-semibold text-brand-ink">
                                        Quiet link →
                                    </span>,
                                ],
                                [
                                    '12px · faint — small meta',
                                    <span className="text-[12px] text-faint">
                                        Small meta
                                    </span>,
                                ],
                                [
                                    '11.5px · faint — fine print ("Saved just now")',
                                    <span className="text-[11.5px] text-faint">
                                        Saved just now
                                    </span>,
                                ],
                                [
                                    '10px · mono 600 caps — micro-label (informational floor)',
                                    <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.1em] text-faint">
                                        Micro-label
                                    </span>,
                                ],
                                [
                                    '9px · mono 600 caps — ambient only, never sole carrier',
                                    <span className="font-mono text-[9px] font-semibold uppercase tracking-[0.14em] text-faint">
                                        Ambient label
                                    </span>,
                                ],
                            ] as [string, React.ReactNode][]
                        ).map(([caption, sample]) => (
                            <div
                                key={caption}
                                className="flex items-baseline justify-between gap-4 border-b border-line pb-2"
                            >
                                {sample}
                                <span className="whitespace-nowrap font-mono text-[10px] text-faint">
                                    {caption}
                                </span>
                            </div>
                        ))}
                        <p className="mt-1 text-[12px] text-faint">
                            Floor: nothing below 9px, ever — 8/8.5/9.5px
                            stragglers in the app are drift to be raised.
                            10px minimum when the text carries information.
                            Inputs render 16px on touch screens (iOS zoom).
                        </p>
                    </div>
                )}
            />

            <Duo
                title="Buttons"
                name="brand pill · dark pill · quiet · icon"
                render={() => (
                    <div className="flex flex-wrap items-center gap-3">
                        <button
                            type="button"
                            className="cursor-pointer rounded-full border-0 bg-brand px-4 py-1.5 font-[inherit] text-[12px] font-semibold text-brand-foreground"
                        >
                            Save changes
                        </button>
                        <button
                            type="button"
                            className="cursor-default rounded-full border-0 bg-brand/40 px-4 py-1.5 font-[inherit] text-[12px] font-semibold text-brand-foreground"
                        >
                            Disabled
                        </button>
                        <button
                            type="button"
                            className="cursor-pointer rounded-full border-0 bg-foreground px-4 py-1.5 font-[inherit] text-[12px] font-semibold text-background"
                        >
                            Dark pill →
                        </button>
                        <button
                            type="button"
                            className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[12.5px] font-semibold text-brand-ink"
                        >
                            Quiet link →
                        </button>
                        <button
                            type="button"
                            className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[12.5px] font-semibold text-attention"
                        >
                            Delete
                        </button>
                        <button
                            type="button"
                            title="Icon button"
                            className="flex h-[36px] w-[36px] cursor-pointer items-center justify-center rounded-full border-0 bg-transparent text-faint hover:bg-secondary hover:text-foreground"
                        >
                            <IconMore />
                        </button>
                    </div>
                )}
            />

            <Duo
                title="Toolbar controls"
                name=".ps-pill · SearchField · FilterMenu · date input"
                render={() => (
                    <div className="flex flex-wrap items-center gap-3">
                        <button
                            type="button"
                            className="ps-pill cursor-pointer px-4 font-[inherit] text-[12.5px] font-semibold text-foreground"
                        >
                            Show more
                        </button>
                        <SearchField
                            value={search}
                            onChange={setSearch}
                            placeholder="Search…"
                            className="w-[200px]"
                        />
                        <FilterMenu
                            dimensions={[
                                dateDimension,
                                {
                                    key: 'lead',
                                    label: 'Lead',
                                    icon: (
                                        <span className="font-mono text-[10px]">
                                            @
                                        </span>
                                    ),
                                    values: ['aubrey', 'nikolai', 'josh'],
                                },
                            ]}
                            onAdd={(f) =>
                                toast(`${f.label} is ${f.value}`)
                            }
                        />
                        <input
                            type="date"
                            className="rounded-[8px] border border-border bg-transparent px-2 py-1.5 font-[inherit] text-[13px] text-foreground"
                        />
                    </div>
                )}
            />

            <Duo
                title="Chips & tags"
                name="filter chip · StatusChip · mono tags"
                render={() => (
                    <div className="flex flex-wrap items-center gap-2.5">
                        <span className="inline-flex items-center gap-1.5 rounded-full border-[1.5px] border-dashed border-border px-[11px] py-[5px] text-xs text-muted-foreground">
                            Date is This month
                            <button
                                type="button"
                                aria-label="Remove filter"
                                className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-faint hover:text-foreground"
                            >
                                ×
                            </button>
                        </span>
                        <StatusChip status="pending" />
                        <StatusChip status="in_production" />
                        <StatusChip status="show_ready" />
                        <StatusChip status="wrapped" />
                        {monoTag('group')}
                        {monoTag('borrowed')}
                        {monoTag('archived')}
                    </div>
                )}
            />

            <Duo
                title="Fields"
                name=".ps-field · .ps-input · Switch · checkbox"
                render={() => (
                    <div className="flex max-w-[340px] flex-col gap-4">
                        <input
                            className="ps-field"
                            placeholder="Boxed field (auth screens)"
                        />
                        <input
                            className="ps-input"
                            placeholder="Underline input (config pages)"
                        />
                        <label className="flex items-center justify-between text-[13px] text-muted-foreground">
                            Switch
                            <Switch
                                checked={switchOn}
                                onCheckedChange={setSwitchOn}
                            />
                        </label>
                        <label className="flex cursor-pointer items-center gap-2.5 text-[13px]">
                            <input
                                type="checkbox"
                                checked={checked}
                                onChange={(e) => setChecked(e.target.checked)}
                                className="h-4 w-4 accent-[hsl(var(--brand))]"
                            />
                            Checkbox row
                        </label>
                    </div>
                )}
            />

            <Duo
                title="Rows & tables"
                name="ps-label header · border rows · hover actions"
                render={() => (
                    <div>
                        <div className="ps-label border-b border-border pb-2 text-left">
                            Activation
                        </div>
                        {[
                            ['Marfa Spirit Co Vintage Booth', 'Marfa, TX'],
                            ['Toyota Music Den — Austin', 'Moody Amphitheater'],
                        ].map(([name, meta], i) => (
                            <div
                                key={name}
                                className="group flex cursor-pointer items-center gap-3 border-b border-line py-3 hover:bg-sunk"
                            >
                                <div className="min-w-0 flex-1">
                                    <div className="truncate text-[13.5px] font-semibold">
                                        {name}
                                    </div>
                                    <div className="mt-0.5 truncate text-[12px] text-faint">
                                        {meta}
                                    </div>
                                </div>
                                <span className="hidden gap-3 text-[12px] font-semibold text-muted-foreground group-hover:inline-flex">
                                    <span>Clone</span>
                                    <span className="text-attention">
                                        Archive
                                    </span>
                                </span>
                                {i === 0 && (
                                    <span className="text-[14px] text-faint">
                                        →
                                    </span>
                                )}
                            </div>
                        ))}
                        <div className="mt-3 flex items-center justify-between text-xs text-faint">
                            <span>2 of 208 activations</span>
                            <button
                                type="button"
                                className="ps-pill cursor-pointer px-4 font-[inherit] text-[12.5px] font-semibold text-foreground"
                            >
                                Show more
                            </button>
                        </div>
                    </div>
                )}
            />

            <Duo
                title="Menus & popovers"
                name="SwitcherMenu (account) · Jump panel"
                render={() => (
                    <div className="flex flex-wrap items-start gap-4">
                        <MenuReplica />
                        <JumpReplica />
                    </div>
                )}
            />

            <Duo
                title="Dock"
                name="app dock · event dock w/ back"
                render={() => <DockReplica />}
            />

            <Duo
                title="States"
                name="skeleton · empty · error · saved · toast"
                render={() => (
                    <div className="flex flex-col gap-4">
                        <div className="h-12 animate-pulse rounded-[12px] bg-secondary" />
                        <p className="text-[13px] text-muted-foreground">
                            No flows — captures publish unprocessed.{' '}
                            <span className="cursor-pointer font-semibold text-brand-ink">
                                Add flow →
                            </span>
                        </p>
                        <span className="text-[12.5px] font-semibold text-attention">
                            Save failed — try again
                        </span>
                        <span className="text-[11.5px] text-faint">
                            Saved just now
                        </span>
                        <button
                            type="button"
                            onClick={() => toast('Assignment saved')}
                            className="ps-pill w-fit cursor-pointer px-4 font-[inherit] text-[12.5px] font-semibold text-foreground"
                        >
                            Fire a toast
                        </button>
                    </div>
                )}
            />

            <Duo
                title="Live elements"
                name=".ps-pulse · Sparkline · stat tiles"
                render={() => (
                    <div className="flex flex-col gap-5">
                        <div className="flex items-center gap-6">
                            <span className="flex items-center gap-2 font-mono text-[10px] font-semibold tracking-[0.1em] text-good">
                                <span className="ps-pulse" />
                                ONLINE
                            </span>
                            <span className="flex items-center gap-2 font-mono text-[10px] font-semibold tracking-[0.1em] text-attention">
                                <span className="ps-pulse ps-pulse-live" />
                                LIVE · 2H 41M
                            </span>
                        </div>
                        <div className="grid grid-cols-3 gap-4">
                            {[
                                ['Photos', '2,417'],
                                ['Devices', '6/6'],
                                ['Share rate', '71%'],
                            ].map(([label, value]) => (
                                <div key={label}>
                                    <div className="font-mono text-[9px] font-semibold uppercase tracking-[0.14em] text-faint">
                                        {label}
                                    </div>
                                    <div className="mt-1 text-[19px] font-bold tabular-nums tracking-[-0.01em]">
                                        {value}
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div>
                            <Sparkline
                                series={SAMPLE_SERIES}
                                className="h-7"
                            />
                            <Sparkline
                                series={SAMPLE_SERIES}
                                variant="area"
                                className="mt-2 h-[52px]"
                            />
                        </div>
                    </div>
                )}
            />
        </main>
    );
}
